import useInvitationSelectionStore from '@/stores/invitation-selection-store';
import { ActionBar, Button, Portal, Text } from '@chakra-ui/react';
import { CloseButton } from '@/components/ui/close-button';
import { Tooltip } from '@/components/ui/tooltip';
import { BULK_INVITATION_OUTREACH_LIMIT } from '@fluenceur/types';
import { Mail, RefreshDouble, Trash } from 'iconoir-react';
import { useTranslation } from 'react-i18next';

interface InvitationBulkActionBarProps {
  /** Opens the mailbox composer for a plain email to the selection. */
  onEmail: () => void;
  /** Opens the mailbox composer for a follow-up to the selection. */
  onResend: () => void;
  onCancel: () => void;
}

/**
 * Floating action bar for the invitations table. Opens as soon as a row is
 * selected (or the whole filter is selected) and offers a grouped email and a
 * grouped follow-up, both from the brand's mailbox, and cancel. The count
 * reflects the filter total when select-all-matching is active.
 */
const InvitationBulkActionBar: React.FC<InvitationBulkActionBarProps> = ({
  onEmail,
  onResend,
  onCancel,
}) => {
  const { t } = useTranslation();
  const selectedIds = useInvitationSelectionStore((s) => s.selectedIds);
  const selectAllMatching = useInvitationSelectionStore((s) => s.selectAllMatching);
  const matchingTotal = useInvitationSelectionStore((s) => s.matchingTotal);
  const clearSelection = useInvitationSelectionStore((s) => s.clearSelection);

  const count = selectAllMatching ? matchingTotal : selectedIds.size;
  const isOpen = count > 0;
  // Every item of a grouped email or follow-up leaves from the brand's own
  // mailbox, hence a lower cap than cancel: above it the composer never opens.
  const isAboveEmailLimit = count > BULK_INVITATION_OUTREACH_LIMIT;

  return (
    <ActionBar.Root open={isOpen}>
      <Portal>
        <ActionBar.Positioner zIndex="modal">
          <ActionBar.Content>
            <Text fontWeight="medium" fontSize="15px" color="white">
              {t('invitations.bulkActionBar.selected', { count })}
            </Text>
            <ActionBar.Separator mx={1} />
            <Tooltip
              content={t('invitations.bulkEmail.limit')}
              disabled={!isAboveEmailLimit}
              portalled
              positioning={{ placement: 'top' }}
            >
              <Button
                variant="subtle"
                size="xs"
                h={8}
                minW={8}
                px={3}
                rounded="md"
                onClick={onEmail}
                disabled={isAboveEmailLimit}
                data-action-bar-primary
              >
                <Mail width={16} height={16} />
                {t('invitations.bulkActionBar.email')}
              </Button>
            </Tooltip>
            <Tooltip
              content={t('invitations.bulkEmail.limit')}
              disabled={!isAboveEmailLimit}
              portalled
              positioning={{ placement: 'top' }}
            >
              <Button
                variant="subtle"
                size="xs"
                h={8}
                minW={8}
                px={3}
                rounded="md"
                onClick={onResend}
                disabled={isAboveEmailLimit}
                data-action-bar-primary
              >
                <RefreshDouble width={16} height={16} />
                {t('invitations.bulkActionBar.resend')}
              </Button>
            </Tooltip>
            <Button
              variant="plain"
              color="red.300"
              size="xs"
              h={8}
              minW={8}
              px={3}
              rounded="md"
              onClick={onCancel}
              _hover={{ bg: 'whiteAlpha.200', color: 'red.200' }}
              _active={{ bg: 'whiteAlpha.300' }}
            >
              <Trash width={16} height={16} />
              {t('invitations.bulkActionBar.cancel')}
            </Button>
            <ActionBar.Separator ml={1} />
            <CloseButton
              size="xs"
              h={8}
              minW={8}
              borderColor="whiteAlpha.300"
              color="whiteAlpha.800"
              aria-label={t('invitations.bulkActionBar.clear')}
              onClick={clearSelection}
              _hover={{ bg: 'whiteAlpha.200', color: 'white' }}
              _active={{ bg: 'whiteAlpha.300' }}
            />
          </ActionBar.Content>
        </ActionBar.Positioner>
      </Portal>
    </ActionBar.Root>
  );
};

export default InvitationBulkActionBar;
